import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { PriorityIssue } from "@/types/priority-issues";
import IssueDetails from "./priority-issues/IssueDetails";
import IssueFilters from "./priority-issues/IssueFilters";

export interface PriorityIssuesProps {
  currentCycleStart?: Date;
}

export const priorityIssues: PriorityIssue[] = [
  {
    id: "billing-invoice",
    title: "Invoice Request & Payment Status",
    urgencyLevel: "High",
    reason: "Most common customer inquiry requiring immediate response",
    description: "Customers requesting invoice information, payment status, and billing details",
    detailedSummary: "This is the most frequent type of support request, where customers need information about their invoices, payment status, or general billing questions. Quick resolution is essential for customer satisfaction and cash flow management.",
    department: "Billing",
    responsibleDepartment: "Finance & Support",
    weeklyTrend: "Increasing",
    totalTickets: 245,
    previousCycle: 220,
    workflowTemplate: {
      steps: [
        {
          type: "trigger",
          title: "Invoice Request",
          description: "Customer requests invoice or payment information"
        },
        {
          type: "action",
          title: "Check Payment Status",
          description: "Verify payment information in Stripe"
        },
        {
          type: "action",
          title: "Query CRM",
          description: "Look up customer details in Salesforce"
        }
      ]
    },
    tickets: [
      {
        id: "INV-001",
        title: "Missing Invoice for March Subscription",
        url: "#",
        date: "2024-03-18",
        issueDetails: "Enterprise customer requesting March invoice copy",
        summary: "Customer unable to locate invoice for March subscription payment. Needs urgent copy for accounting purposes."
      },
      {
        id: "INV-002",
        title: "Payment Status Check",
        url: "#",
        date: "2024-03-17",
        issueDetails: "Customer inquiring about payment processing status",
        summary: "Customer made payment 2 days ago but hasn't received confirmation. Needs verification of payment receipt."
      }
    ]
  },
  {
    id: "account-access",
    title: "Account Access & Management",
    urgencyLevel: "High",
    reason: "Critical access issues affecting user productivity",
    description: "Users experiencing account access issues or needing account changes",
    detailedSummary: "Second most common support category involving user access problems, permission changes, and account configuration requests. Quick resolution directly impacts user productivity.",
    department: "Support",
    responsibleDepartment: "Customer Success",
    weeklyTrend: "Stable",
    totalTickets: 210,
    previousCycle: 235,
    workflowTemplate: {
      steps: [
        {
          type: "trigger",
          title: "Account Access Request",
          description: "User reports access issue or requests changes"
        },
        {
          type: "action",
          title: "Check CRM Data",
          description: "Verify account status and permissions"
        },
        {
          type: "condition",
          title: "Verify Identity",
          description: "Request additional verification if needed"
        }
      ]
    },
    tickets: [
      {
        id: "ACC-001",
        title: "Unable to Access Admin Dashboard",
        url: "#",
        date: "2024-03-18",
        issueDetails: "Admin user cannot access management features",
        summary: "Senior admin reporting complete loss of access to administrative features after recent role update."
      }
    ]
  }
];

const PriorityIssues = ({ currentCycleStart }: PriorityIssuesProps) => {
  const [expandedCards, setExpandedCards] = useState<string[]>([]);
  const [expandedTickets, setExpandedTickets] = useState<string[]>([]);

  const toggleCard = (issueId: string) => {
    setExpandedCards(prev => 
      prev.includes(issueId) 
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  const toggleTickets = (issueId: string) => {
    setExpandedTickets(prev => 
      prev.includes(issueId) 
        ? prev.filter(id => id !== issueId)
        : [...prev, issueId]
    );
  };

  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="text-lg font-medium">Priority Issues</h3>
        <IssueFilters />
      </div>
      <div className="space-y-4">
        {priorityIssues.map((issue) => (
          <motion.div
            key={issue.id}
            id={`priority-${issue.id}`}
            initial={{ opacity: 0.6 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-gray-200 transition-all duration-300 hover:shadow-md">
              <CardHeader 
                className="flex flex-row items-center justify-between space-y-0 pb-2 cursor-pointer"
                onClick={() => toggleCard(issue.id)}
              >
                <div>
                  <CardTitle className="text-xl font-semibold">
                    {issue.title}
                  </CardTitle>
                  <CardDescription className="mt-2">
                    {issue.description}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`rounded-full px-3 py-1 text-sm ${
                    issue.urgencyLevel === "High" 
                      ? "bg-red-100 text-red-700" 
                      : "bg-yellow-100 text-yellow-700"
                  }`}>
                    {issue.urgencyLevel}
                  </span>
                  {expandedCards.includes(issue.id) ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </CardHeader>
              <IssueDetails 
                issue={issue}
                isExpanded={expandedCards.includes(issue.id)}
                showTickets={expandedTickets.includes(issue.id)}
                onToggleTickets={() => toggleTickets(issue.id)}
              />
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default PriorityIssues;